import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../ThemeContext";
import Marquee from "../magicui/Marquee";
import { PROVIDER_VIDEOS, pickRandom } from "../../config/providerVideos";

const CLIP_COUNT = 10;
// tile (180px) + its horizontal margins (16px) + the track gap (16px)
const TILE_PITCH = 212;
// scroll speed in px/s, so the pace is the same however many clips there are
const SPEED = 45;

/**
 * A single 9:16 clip. The <video> is only mounted while the tile is near the
 * viewport (and torn down once it leaves), so the browser never streams more
 * than a handful of clips at once even though the marquee holds dozens.
 */
function VideoTile({ src }: { src: string }) {
    const tileRef = useRef<HTMLDivElement>(null);
    const [isNear, setIsNear] = useState(false);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        const tile = tileRef.current;
        if (!tile) return;
        if (typeof IntersectionObserver === "undefined") {
            setIsNear(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsNear(entry.isIntersecting);
                if (!entry.isIntersecting) setIsReady(false);
            },
            { rootMargin: "200px 400px" }
        );
        observer.observe(tile);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={tileRef}
            className="mx-2 h-80 w-[180px] shrink-0 overflow-hidden rounded-xl bg-gradient-to-b from-slate-200/70 to-slate-300/50 dark:from-slate-800/60 dark:to-slate-900/60"
        >
            {isNear && (
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    controls={false}
                    aria-hidden="true"
                    tabIndex={-1}
                    onLoadedData={() => setIsReady(true)}
                    className={`h-full w-full object-cover transition-opacity duration-500 ${isReady ? "opacity-100" : "opacity-0"}`}
                >
                    {/* The storage server labels these files binary/octet-stream; declaring the type keeps Safari happy */}
                    <source src={src} type="video/mp4" />
                </video>
            )}
        </div>
    );
}

/** Visitors who asked their browser to save data shouldn't stream dozens of full-length clips. */
const prefersLessData = () =>
    typeof navigator !== "undefined" && Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);

export function VideoMarquee() {
    const { theme } = useTheme();
    const fade = theme === "dark" ? "from-[#0a0a0a]" : "from-white";

    // A different random handful each visit
    const clips = useMemo(() => pickRandom(PROVIDER_VIDEOS, CLIP_COUNT), []);
    const loopWidth = clips.length * TILE_PITCH;

    // The loop only looks seamless if one copy is at least as wide as the screen, so add copies on very wide screens
    const [copies, setCopies] = useState(3);
    useEffect(() => {
        const update = () => setCopies(Math.max(2, Math.ceil(window.innerWidth / loopWidth) + 1));
        update();
        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [loopWidth]);

    if (prefersLessData()) return null;

    return (
        <div className="relative flex w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-transparent pt-8 pb-2 md:pt-32 md:pb-4">
            {/* Every copy of the clip row is identical and exactly one row + gap wide, so the loop restarts invisibly */}
            <Marquee pauseOnHover repeat={copies} style={{ ["--duration" as string]: `${Math.round(loopWidth / SPEED)}s` }}>
                {clips.map((src) => (
                    <VideoTile key={src} src={src} />
                ))}
            </Marquee>
            <div className={`pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r ${fade}`}></div>
            <div className={`pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l ${fade}`}></div>
        </div>
    );
}

export default VideoMarquee;
